import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { useIdentity } from 'context/identity-provider';
import styles from './login-view.module.scss';

interface LocationState {
  from?: string;
}

const LoginView: React.FC = () => {
  const { isAuth, isLoading, signIn } = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as LocationState | null)?.from ?? '/portal';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isLoading) return null;
  if (isAuth) return <Navigate to={returnTo} replace />;

  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await signIn(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.loginRoot}>
      <Paper className={styles.card} elevation={6}>
        <Typography variant="h5" component="h1" gutterBottom>
          Admin Portal
        </Typography>
        <Box component="form" onSubmit={submitHandler} noValidate>
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
            required
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          {error && <Alert severity="error" className={styles.error}>{error}</Alert>}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={styles.submit}
            disabled={isSubmitting || !email || !password}
          >
            {isSubmitting ? <CircularProgress size={22} /> : 'Sign in'}
          </Button>
        </Box>
        <Link to="/" className={styles.homeLink}>← back to site</Link>
      </Paper>
    </div>
  );
};

export default LoginView;
